import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, MapPin, ArrowLeft, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const AddressInput = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDate, hourlyRate, propertyType, propertySize, floorType, crewRecommendation } = location.state || {};
  
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [originZip, setOriginZip] = useState('');
  const [destinationZip, setDestinationZip] = useState('');
  const [useFullAddress, setUseFullAddress] = useState(false);

  const isFormValid = useFullAddress 
    ? originAddress.trim() && destinationAddress.trim()
    : originZip.trim() && destinationZip.trim();

  const handleContinue = () => {
    if (isFormValid) {
      const origin = useFullAddress ? originAddress : originZip;
      const destination = useFullAddress ? destinationAddress : destinationZip;
      
      navigate('/route-map', {
        state: {
          selectedDate,
          hourlyRate,
          propertyType,
          propertySize,
          floorType,
          crewRecommendation,
          originAddress: origin,
          destinationAddress: destination,
          addressType: useFullAddress ? 'full' : 'zip'
        }
      });
    }
  };

  const handleGoBack = () => {
    navigate('/floor-selection', { 
      state: { selectedDate, hourlyRate, propertyType, propertySize },
      replace: true
    });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                alt="Emisa Movers Logo"
                className="h-28 w-28 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emisa Movers</h1>
                <p className="text-xs text-amber-700 font-medium">GETS IT DONE ON TIME</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="tel:7134879097"
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform"
              >
                <Phone className="h-4 w-4" />
                (713) 487-9097
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); window.location.href = '/#book-now'; }} className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform">Book It NOW</a>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 hover:shadow-xl hover:-translate-y-1 text-white border-gray-600 transition-all duration-300 transform"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Floor Selection
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Booking Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-2 mb-4 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div>
              <span className="text-gray-600">Date:</span>
              <div className="font-medium text-teal-600 text-xs">{selectedDate}</div>
            </div>
            <div>
              <span className="text-gray-600">Rate:</span>
              <div className="font-medium text-red-600 text-xs">${hourlyRate}/h</div>
            </div>
            <div>
              <span className="text-gray-600">Property:</span>
              <div className="font-medium text-teal-600 text-xs capitalize">{propertyType}</div>
            </div>
            <div>
              <span className="text-gray-600">Size:</span>
              <div className="font-medium text-teal-600 text-xs">{propertySize} sqft</div>
            </div>
            <div>
              <span className="text-gray-600">Crew:</span>
              <div className="font-medium text-teal-600 text-xs">{crewRecommendation?.men} movers</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Location Information
          </h1>
          <p className="text-sm sm:text-base text-gray-700">
            Provide your pickup and delivery locations for route planning
          </p>
        </div>

        {/* Address Type Toggle */}
        <div className="mb-4">
          <div className="flex justify-center">
            <div className="bg-white rounded-lg p-1 shadow-sm border">
              <Button
                variant={!useFullAddress ? "default" : "ghost"}
                onClick={() => setUseFullAddress(false)}
                className={`rounded-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${!useFullAddress ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'hover:bg-gray-100'}`}
              >
                <MapPin className="h-4 w-4 mr-2" />
                Zip Codes Only
              </Button>
              <Button
                variant={useFullAddress ? "default" : "ghost"}
                onClick={() => setUseFullAddress(true)}
                className={`rounded-md transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md ${useFullAddress ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'hover:bg-gray-100'}`}
              >
                <Home className="h-4 w-4 mr-2" />
                Full Addresses
              </Button>
            </div>
          </div>
        </div>

        {/* Address Forms */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* Origin */}
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-teal-600" />
                Pickup Location
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {useFullAddress ? (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="originAddress">Full Address</Label>
                    <Input
                      id="originAddress"
                      placeholder="123 Main St, City, State 12345"
                      value={originAddress}
                      onChange={(e) => setOriginAddress(e.target.value)}
                      className="mt-1 focus:ring-teal-600 focus:border-teal-600"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="originZip">Zip Code</Label>
                  <Input
                    id="originZip"
                    placeholder="12345"
                    value={originZip}
                    onChange={(e) => setOriginZip(e.target.value)}
                    className="mt-1 focus:ring-blue-600 focus:border-blue-600"
                    maxLength={5}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Destination */}
          <Card>
            <CardHeader className="p-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-teal-600" />
                Delivery Location
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {useFullAddress ? (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="destinationAddress">Full Address</Label>
                    <Input
                      id="destinationAddress"
                      placeholder="456 Oak Ave, City, State 12345"
                      value={destinationAddress}
                      onChange={(e) => setDestinationAddress(e.target.value)}
                      className="mt-1 focus:ring-teal-600 focus:border-teal-600"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="destinationZip">Zip Code</Label>
                  <Input
                    id="destinationZip"
                    placeholder="54321"
                    value={destinationZip}
                    onChange={(e) => setDestinationZip(e.target.value)}
                    className="mt-1 focus:ring-blue-600 focus:border-blue-600"
                    maxLength={5}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Information Note */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 mb-4">
          <div className="flex gap-2 items-start">
            <div className="text-teal-600 text-sm">ℹ️</div>
            <div>
              <p className="text-xs text-gray-700">
                {useFullAddress 
                  ? "Full addresses help us provide more accurate time and cost estimates."
                  : "Zip codes provide quick estimates. You can provide exact addresses later when booking."
                }
              </p>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <Button
            onClick={handleContinue}
            disabled={!isFormValid}
            size="lg"
            className="bg-red-600 hover:bg-red-700 hover:shadow-xl hover:-translate-y-1 hover:scale-105 text-white px-8 py-3 text-base disabled:opacity-50 transition-all duration-300 transform"
          >
            Continue to Route Planning
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddressInput;